import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useOnlineStatus } from './useOnlineStatus'
import { useAuth } from '@/contexts/AuthProvider'
import { notifyLeaveSubmitted } from '@/lib/leaveNotifications'
import {
  cacheLeaveBalance,
  getCachedLeaveBalance,
  cacheLeaveRequests,
  createOfflineLeaveRequest,
} from '../db/mobileDbOperations'

export function useMobileAttendance(userProfileId, companyId) {
  const isOnline = useOnlineStatus()
  const { userProfile } = useAuth()
  const [leaveBalance, setLeaveBalance] = useState(null)
  const [leaveRequests, setLeaveRequests] = useState([])
  const [leaveTypes, setLeaveTypes] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    if (!userProfileId || !companyId) return
    setLoading(true)

    if (isOnline) {
      try {
        const { data, error } = await supabase.rpc('app_get_attendance_data_v2', {
          p_user_id: userProfileId,
          p_company_id: companyId,
        })

        if (error) throw error

        const balance = data?.leave_balance || null
        const requests = data?.leave_requests || []
        const types = data?.leave_types || []

        setLeaveBalance(balance)
        setLeaveRequests(requests)
        setLeaveTypes(types)

        if (balance) await cacheLeaveBalance([balance])
        await cacheLeaveRequests(requests)
      } catch (err) {
        console.error('[Attendance] Error:', err)
      }
    } else {
      try {
        const cached = await getCachedLeaveBalance()
        if (cached?.length) setLeaveBalance(cached[0])
      } catch {
        // No cached data
      }
    }

    setLoading(false)
  }, [userProfileId, companyId, isOnline])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const submitLeaveRequest = async ({ leaveType, startDate, endDate, reason }) => {
    if (isOnline) {
      const { data, error } = await supabase.rpc('app_submit_leave_request_v3', {
        p_user_id: userProfileId,
        p_leave_type: leaveType,
        p_start_date: startDate,
        p_end_date: endDate,
        p_reason: reason || null,
      })

      if (error) throw error
      setLeaveRequests(prev => [data, ...prev])
      // The approvers are emailed by the database; this tells the requester.
      if (data?.status === 'pending') notifyLeaveSubmitted(data, userProfile)
      return data
    } else {
      await createOfflineLeaveRequest({ leaveType, startDate, endDate, reason })
      return { leaveType, startDate, endDate, reason, status: 'pending', offline: true }
    }
  }

  return {
    leaveBalance,
    leaveRequests,
    leaveTypes,
    loading,
    refetch: fetchAll,
    submitLeaveRequest,
  }
}
